import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, Download, Upload, FileSpreadsheet, AlertCircle,
  CheckCircle2, Loader2, AlertTriangle, Trash2, Pencil, X, Check, Users
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function EditableCell({ value, onChange, type = 'text', options, placeholder }) {
  if (options) {
    return (
      <select
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        className="w-full text-xs border border-slate-200 rounded px-1.5 py-1 bg-white focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
        data-testid="editable-select"
      >
        <option value="">-</option>
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  }
  return (
    <input
      type={type}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full text-xs border border-slate-200 rounded px-1.5 py-1 focus:ring-1 focus:ring-blue-400 focus:border-blue-400"
      data-testid="editable-input"
    />
  );
}

export default function CRMBulkImport({ user }) {
  const navigate = useNavigate();
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [refLists, setRefLists] = useState(null);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const response = await axios.get(`${API}/crm/bulk-import/template?user_id=${user.user_id}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'import_pelatwn_template.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Το πρότυπο κατέβηκε');
    } catch {
      toast.error('Σφάλμα κατά τη λήψη');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Μόνο αρχεία Excel (.xlsx) γίνονται δεκτά');
      e.target.value = '';
      return;
    }

    setParsing(true);
    setParsedData(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/crm/bulk-import/parse?user_id=${user.user_id}`, formData);
      setParsedData(res.data.rows);
      setRefLists(res.data.reference_lists);
      const summary = res.data;
      if (summary.error_rows > 0) {
        toast.warning(`${summary.total_rows} γραμμές, ${summary.error_rows} με σφάλματα. Ελέγξτε και διορθώστε.`);
      } else if (summary.duplicate_rows > 0) {
        toast.warning(`${summary.total_rows} γραμμές, ${summary.duplicate_rows} πιθανά διπλότυπα.`);
      } else {
        toast.success(`${summary.total_rows} γραμμές φορτώθηκαν. Ελέγξτε και πατήστε Εισαγωγή.`);
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Σφάλμα κατά την ανάγνωση αρχείου';
      toast.error(msg);
    } finally {
      setParsing(false);
      e.target.value = '';
    }
  };

  const updateRow = useCallback((index, field, value) => {
    setParsedData(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      // Clear errors when user edits (they'll re-validate on submit)
      if (updated[index].errors?.length > 0) {
        updated[index].errors = [];
      }
      return updated;
    });
  }, []);

  const deleteRow = useCallback((index) => {
    setParsedData(prev => prev.filter((_, i) => i !== index));
    toast.success('Η γραμμή αφαιρέθηκε');
  }, []);

  const confirmImport = async () => {
    if (!parsedData || parsedData.length === 0) {
      toast.error('Δεν υπάρχουν δεδομένα για εισαγωγή');
      return;
    }

    // Check for rows with errors
    const rowsWithErrors = parsedData.filter(r => r.errors?.length > 0);
    if (rowsWithErrors.length > 0) {
      toast.error(`${rowsWithErrors.length} γραμμές έχουν σφάλματα. Διορθώστε τις πρώτα.`);
      return;
    }

    // Check required fields client-side
    const invalid = parsedData.filter(r => !r.customer_type || !r.company_name);
    if (invalid.length > 0) {
      toast.error(`${invalid.length} γραμμές δεν έχουν Τύπο ή Επωνυμία.`);
      return;
    }

    setImporting(true);
    try {
      const payload = {
        customers: parsedData.map(r => ({
          customer_type: r.customer_type,
          company_name: r.company_name,
          contact_person: r.contact_person || '',
          email: r.email || '',
          phone: r.phone || '',
          mobile: r.mobile || '',
          address: r.address || '',
          tax_id: r.tax_id || '',
          kad: r.kad || '',
          region_id: r.region_id || '',
          program_id: r.program_id || '',
          business_category: r.business_category || '',
          notes: r.notes || ''
        }))
      };
      const res = await axios.post(`${API}/crm/bulk-import/confirm?user_id=${user.user_id}`, payload);
      setResult(res.data);
      setParsedData(null);

      if (res.data.imported > 0 && res.data.skipped_duplicates === 0 && res.data.errors_count === 0) {
        toast.success(`Εισήχθησαν ${res.data.imported} πελάτες επιτυχώς!`);
      } else if (res.data.imported > 0) {
        toast.warning(`Εισήχθησαν ${res.data.imported}, παραλείφθηκαν ${res.data.skipped_duplicates} διπλότυπα`);
      } else {
        toast.error('Κανένας πελάτης δεν εισήχθη.');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Σφάλμα κατά την εισαγωγή';
      toast.error(msg);
    } finally {
      setImporting(false);
    }
  };

  const regionOptions = refLists?.regions
    ? [...new Map(refLists.regions.map(r => [r.region_id, r])).values()]
        .map(r => ({ value: r.region_id, label: r.name }))
    : [];
  const programOptions = refLists?.programs
    ? [...new Map(refLists.programs.map(p => [p.program_id, p])).values()]
        .map(p => ({ value: p.program_id, label: p.name }))
    : [];
  const categoryOptions = refLists?.categories
    ? [...new Set(refLists.categories)].map(c => ({ value: c, label: c }))
    : [];
  const typeOptions = [
    { value: 'existing', label: 'Υφιστάμενη' },
    { value: 'new', label: 'Νέα' }
  ];

  const validCount = parsedData ? parsedData.filter(r => !r.errors?.length).length : 0;
  const errorCount = parsedData ? parsedData.filter(r => r.errors?.length > 0).length : 0;
  const dupCount = parsedData ? parsedData.filter(r => r.is_duplicate).length : 0;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-[1400px] mx-auto p-4 md:p-8 pt-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Button variant="ghost" onClick={() => navigate('/crm')} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Πίσω
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Μαζική Εισαγωγή Πελατών</h1>
            <p className="text-sm text-slate-500">Εισαγωγή πελατών από αρχείο Excel</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Step 1 */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-sm shrink-0">1</div>
              <div className="flex-1">
                <h2 className="font-semibold text-slate-800 mb-1">Κατεβάστε το Πρότυπο Excel</h2>
                <p className="text-sm text-slate-500 mb-2">
                  Περιλαμβάνει φύλλα αναφοράς με τις αποδεκτές τιμές για Περιοχή, Πρόγραμμα και Κατηγορία.
                </p>
                <Button onClick={downloadTemplate} variant="outline" data-testid="download-template-btn">
                  <Download className="h-4 w-4 mr-2" />
                  Λήψη Προτύπου (.xlsx)
                </Button>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-sm shrink-0">2</div>
              <div className="flex-1">
                <h2 className="font-semibold text-slate-800 mb-1">Ανεβάστε το Αρχείο</h2>
                <p className="text-sm text-slate-500 mb-4">
                  Το σύστημα θα αναγνωρίσει αυτόματα τιμές λιστών (π.χ. περιοχή, πρόγραμμα) ακόμα κι αν υπάρχουν μικρά λάθη.
                </p>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={parsing}
                    data-testid="import-file-input"
                  />
                  <span className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                    parsing ? 'bg-slate-100 text-slate-400' : 'bg-slate-900 text-white hover:bg-slate-800 cursor-pointer'
                  }`}>
                    {parsing ? (
                      <><Loader2 className="h-4 w-4 animate-spin" /> Ανάγνωση...</>
                    ) : (
                      <><Upload className="h-4 w-4" /> Φόρτωση Αρχείου</>
                    )}
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Step 3: Preview Table */}
          {parsedData && parsedData.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="preview-section">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-sm shrink-0">3</div>
                <div className="flex-1">
                  <h2 className="font-semibold text-slate-800 mb-1">Έλεγχος & Επεξεργασία</h2>
                  <p className="text-sm text-slate-500">
                    Ελέγξτε, επεξεργαστείτε ή διαγράψτε γραμμές πριν την εισαγωγή.
                  </p>
                </div>
              </div>

              {/* Summary badges */}
              <div className="flex flex-wrap gap-3 mb-4 ml-12">
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 rounded-lg text-sm">
                  <Users className="h-4 w-4 text-slate-500" />
                  <span className="font-medium text-slate-700" data-testid="preview-total">{parsedData.length}</span>
                  <span className="text-slate-500">σύνολο</span>
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 rounded-lg text-sm">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <span className="font-medium text-emerald-700" data-testid="preview-valid">{validCount}</span>
                  <span className="text-emerald-600">έτοιμες</span>
                </div>
                {errorCount > 0 && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 rounded-lg text-sm">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <span className="font-medium text-red-700" data-testid="preview-errors">{errorCount}</span>
                    <span className="text-red-600">σφάλματα</span>
                  </div>
                )}
                {dupCount > 0 && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 rounded-lg text-sm">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <span className="font-medium text-amber-700" data-testid="preview-duplicates">{dupCount}</span>
                    <span className="text-amber-600">πιθανά διπλότυπα</span>
                  </div>
                )}
              </div>

              {/* Table */}
              <div className="overflow-x-auto ml-0 border border-slate-200 rounded-lg">
                <table className="w-full text-xs" data-testid="preview-table">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 w-8">#</th>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 min-w-[90px]">Τύπος*</th>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 min-w-[140px]">Επωνυμία*</th>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 min-w-[120px]">Υπεύθυνος</th>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 min-w-[130px]">Email</th>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 min-w-[100px]">Τηλέφωνο</th>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 min-w-[100px]">Κινητό</th>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 min-w-[90px]">ΑΦΜ</th>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 min-w-[120px]">Περιοχή</th>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 min-w-[150px]">Πρόγραμμα</th>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 min-w-[120px]">Κατηγορία</th>
                      <th className="px-2 py-2 text-left font-semibold text-slate-600 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedData.map((row, idx) => {
                      const hasErrors = row.errors?.length > 0;
                      const isDup = row.is_duplicate;
                      let rowBg = '';
                      if (hasErrors) rowBg = 'bg-red-50/60';
                      else if (isDup) rowBg = 'bg-amber-50/60';

                      return (
                        <React.Fragment key={idx}>
                          <tr className={`border-b border-slate-100 hover:bg-slate-50/50 ${rowBg}`} data-testid={`preview-row-${idx}`}>
                            <td className="px-2 py-1.5 text-slate-400 font-mono">{idx + 1}</td>
                            <td className="px-1 py-1">
                              <EditableCell
                                value={row.customer_type}
                                onChange={v => updateRow(idx, 'customer_type', v)}
                                options={typeOptions}
                              />
                            </td>
                            <td className="px-1 py-1">
                              <EditableCell
                                value={row.company_name}
                                onChange={v => updateRow(idx, 'company_name', v)}
                                placeholder="Επωνυμία*"
                              />
                            </td>
                            <td className="px-1 py-1">
                              <EditableCell
                                value={row.contact_person}
                                onChange={v => updateRow(idx, 'contact_person', v)}
                              />
                            </td>
                            <td className="px-1 py-1">
                              <EditableCell
                                value={row.email}
                                onChange={v => updateRow(idx, 'email', v)}
                                type="email"
                              />
                            </td>
                            <td className="px-1 py-1">
                              <EditableCell
                                value={row.phone}
                                onChange={v => updateRow(idx, 'phone', v)}
                              />
                            </td>
                            <td className="px-1 py-1">
                              <EditableCell
                                value={row.mobile}
                                onChange={v => updateRow(idx, 'mobile', v)}
                              />
                            </td>
                            <td className="px-1 py-1">
                              <EditableCell
                                value={row.tax_id}
                                onChange={v => updateRow(idx, 'tax_id', v)}
                              />
                            </td>
                            <td className="px-1 py-1">
                              <EditableCell
                                value={row.region_id}
                                onChange={v => updateRow(idx, 'region_id', v)}
                                options={regionOptions}
                              />
                            </td>
                            <td className="px-1 py-1">
                              <EditableCell
                                value={row.program_id}
                                onChange={v => updateRow(idx, 'program_id', v)}
                                options={programOptions}
                              />
                            </td>
                            <td className="px-1 py-1">
                              <EditableCell
                                value={row.business_category}
                                onChange={v => updateRow(idx, 'business_category', v)}
                                options={categoryOptions}
                              />
                            </td>
                            <td className="px-1 py-1 text-center">
                              <button
                                onClick={() => deleteRow(idx)}
                                className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="Διαγραφή"
                                data-testid={`delete-row-${idx}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </td>
                          </tr>
                          {/* Inline warnings/errors */}
                          {(hasErrors || (row.warnings?.length > 0)) && (
                            <tr className={rowBg}>
                              <td></td>
                              <td colSpan={11} className="px-2 pb-2">
                                {hasErrors && row.errors.map((e, j) => (
                                  <span key={`e-${j}`} className="inline-flex items-center gap-1 text-[10px] text-red-600 mr-3">
                                    <AlertCircle className="h-3 w-3 shrink-0" />{e}
                                  </span>
                                ))}
                                {row.warnings?.map((w, j) => (
                                  <span key={`w-${j}`} className="inline-flex items-center gap-1 text-[10px] text-amber-600 mr-3">
                                    <AlertTriangle className="h-3 w-3 shrink-0" />{w}
                                  </span>
                                ))}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Confirm button */}
              <div className="flex items-center justify-between mt-6 ml-12">
                <p className="text-xs text-slate-400">
                  Οι γραμμές με ίδιο ΑΦΜ ή Επωνυμία δεν θα εισαχθούν (αποφυγή διπλότυπων).
                </p>
                <Button
                  onClick={confirmImport}
                  disabled={importing || parsedData.length === 0}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-6"
                  data-testid="confirm-import-btn"
                >
                  {importing ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Εισαγωγή...</>
                  ) : (
                    <><CheckCircle2 className="h-4 w-4 mr-2" /> Εισαγωγή {validCount} Επαφών</>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Final Results */}
          {result && (
            <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="import-results">
              <h2 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-slate-400" />
                Αποτελέσματα Εισαγωγής
              </h2>

              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="rounded-lg bg-emerald-50 p-3 text-center">
                  <div className="text-2xl font-bold text-emerald-600" data-testid="result-imported">{result.imported}</div>
                  <div className="text-xs text-emerald-600">Εισήχθησαν</div>
                </div>
                <div className={`rounded-lg p-3 text-center ${result.skipped_duplicates > 0 ? 'bg-amber-50' : 'bg-slate-50'}`}>
                  <div className={`text-2xl font-bold ${result.skipped_duplicates > 0 ? 'text-amber-600' : 'text-slate-400'}`} data-testid="result-duplicates">{result.skipped_duplicates}</div>
                  <div className={`text-xs ${result.skipped_duplicates > 0 ? 'text-amber-600' : 'text-slate-400'}`}>Διπλότυπα</div>
                </div>
                <div className={`rounded-lg p-3 text-center ${result.errors_count > 0 ? 'bg-red-50' : 'bg-slate-50'}`}>
                  <div className={`text-2xl font-bold ${result.errors_count > 0 ? 'text-red-600' : 'text-slate-400'}`} data-testid="result-errors">{result.errors_count}</div>
                  <div className={`text-xs ${result.errors_count > 0 ? 'text-red-600' : 'text-slate-400'}`}>Σφάλματα</div>
                </div>
              </div>

              {result.imported > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-emerald-700 mb-2 flex items-center gap-1">
                    <CheckCircle2 className="h-4 w-4" /> Εισήχθησαν
                  </h3>
                  <div className="space-y-1 max-h-40 overflow-auto">
                    {result.imported_customers?.map((c, i) => (
                      <div key={i} className="text-sm text-slate-600 pl-5">{c.company_name}</div>
                    ))}
                  </div>
                </div>
              )}

              {result.skipped_duplicates > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-amber-700 mb-2 flex items-center gap-1">
                    <AlertTriangle className="h-4 w-4" /> Παραλείφθηκαν (Διπλότυπα)
                  </h3>
                  <div className="space-y-1 max-h-40 overflow-auto">
                    {result.duplicates?.map((d, i) => (
                      <div key={i} className="text-sm text-amber-700 pl-5">
                        {d.company_name} - <span className="text-xs text-amber-500">{d.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.errors_count > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-red-700 mb-2 flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" /> Σφάλματα
                  </h3>
                  <div className="space-y-1 max-h-40 overflow-auto">
                    {result.errors?.map((e, i) => (
                      <div key={i} className="text-sm text-red-700 pl-5">
                        {e.company_name} - <span className="text-xs text-red-500">{e.error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-4 flex gap-3">
                <Button variant="outline" onClick={() => navigate('/crm')} data-testid="go-to-crm-btn">
                  Μετάβαση στο CRM
                </Button>
                <Button variant="ghost" onClick={() => { setResult(null); setParsedData(null); }} data-testid="new-import-btn">
                  Νέα Εισαγωγή
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
